import { Children, isValidElement, PropsWithChildren, ReactElement, useEffect } from 'react';
import { styled, Tabs as MuiTabs, TabsProps as MuiTabsProps } from '@mui/material';
import { useTabsContext } from './useTabsContext';

const StyledTabs = styled(MuiTabs)(() => ({
  borderBottom: '1px solid var(--divider-color)',
  padding: '0 var(--spacer-20) !important',
  backgroundColor: 'var(--surface-bg-color)',
  '.MuiTabs-indicator	': {
    backgroundColor: 'var(--primary-color) !important'
  }
}));

export interface TabHeadProps extends MuiTabsProps {
  isScrollable?: boolean | undefined;
  compact?: boolean;
}

export function TabHead({ isScrollable, compact, children }: PropsWithChildren<TabHeadProps>) {
  const { selectedTab, onSelected } = useTabsContext();

  // sets the first tab as active in case the selected tab is removed.
  useEffect(() => {
    const childTabs: ReactElement[] = Children.toArray(children).filter(isValidElement);

    if (childTabs.length === 0) {
      return;
    }

    if (!childTabs.find(x => x.props.value === selectedTab.toString().split('/')[0])) {
      onSelected(null, childTabs[0].props.value);
    }
  }, [Children.toArray(children)?.length]);

  return (
    <StyledTabs
      value={selectedTab}
      onChange={onSelected}
      variant={isScrollable ? 'scrollable' : 'fullWidth'}
      visibleScrollbar={false}
      sx={compact ? { minHeight: '38px' } : null}
      aria-label={`tabs ${isScrollable ? 'scrollable' : ''}`}
      allowScrollButtonsMobile={false}
      scrollButtons={false}
    >
      {children}
    </StyledTabs>
  );
}
